import { z } from 'zod';
import { ExposureWarningSchema } from './exposure';
import { PricingPlanTierSchema, resolvePlanForTier, resolvePlanSuccessFee, } from './pricing-rules';
/**
 * Bid-relay desk contract (Phase 9 CATCH-UP — bid-relay brokerage model).
 *
 * The platform is a BID-RELAY brokerage: a client bids on our site, an ADMIN
 * manually relays chosen bids to the real Swiss auction (upstream tender),
 * marks win/loss and computes the client's debt (bid + CarsFinders
 * commission; the Swiss platform fee is ours, not the client's).
 * `relayStatus` tracks that ladder ON TOP of the legacy user-facing
 * `BidStatus` — the two dimensions are independent:
 * `status` stays the client-visible auction state, `relayStatus` is the
 * back-office relay pipeline state.
 */
/** Relay pipeline states, in ladder order. */
export const RelayStatusSchema = z.enum([
    'captured', // client bid recorded on our site (default at capture time)
    'queued', // admin queued it for relay to the upstream auction
    'relayed', // admin confirmed placing it upstream (upstreamRef recorded)
    'won', // upstream tender won — client debt computed
    'lost', // upstream tender lost
    'cancelled', // relay cancelled (client withdrew / admin skipped)
    'expired', // tender closed while still captured/queued — never relayed (Phase 10 cron)
    // Phase 13: the operator TRIED to place it upstream and the real auction
    // refused (reserve not met, lot withdrawn, account limit, ...). Terminal, and
    // deliberately distinct from `expired` (never attempted) and `lost` (placed
    // and outbid at the tender): each names a different failure the client is
    // told about differently. See RelayRejectReasonSchema.
    'upstream_rejected',
]);
export const RELAY_STATUSES = RelayStatusSchema.options;
/**
 * Why the upstream auction refused a bid the operator tried to relay
 * (Phase 13 `POST /admin/relay-queue/:bidId/reject`). Written to
 * `bids.relayRejectReason` and carried on the client's `bid.status_changed`
 * so the template can say "менеджер не зміг передати ставку: <reason>" rather
 * than a bare "lot not won".
 */
export const RelayRejectReasonSchema = z.enum([
    'amount_below_reserve',
    'lot_withdrawn',
    'account_limit',
    'other',
]);
export const RELAY_REJECT_REASONS = RelayRejectReasonSchema.options;
/**
 * How long an operator's claim on a relay-queue row stays exclusive. After
 * this a colleague may take the row over WITHOUT a forced release — the
 * original operator walked away (lunch, a crash, a closed tab) and a lot with
 * a tender clock cannot wait for them to come back.
 */
export const RELAY_CLAIM_STALE_MS = 30 * 60 * 1000;
/**
 * Relay states in which an operator may CLAIM a row (Phase 13). A claim is a
 * work lock, not a state: it says "I am the one relaying / recording this
 * bid", so it covers the whole span from capture to the recorded outcome —
 * `relayed` included, because two operators recording conflicting outcomes
 * for one upstream bid is exactly the race the claim exists to prevent.
 */
export const RELAY_CLAIMABLE_STATES = ['captured', 'queued', 'relayed'];
/**
 * A bid document written before the relay desk existed has no `relayStatus`.
 * Its effective state is 'captured' — the default at capture time.
 */
export function effectiveRelayStatus(relayStatus) {
    const parsed = RelayStatusSchema.safeParse(relayStatus);
    return parsed.success ? parsed.data : 'captured';
}
export const RELAY_TRANSITIONS = {
    queue: ['captured'],
    'confirm-relay': ['captured', 'queued'],
    outcome: ['relayed'],
    'cancel-relay': ['captured', 'queued', 'relayed'],
    // Phase 13: the upstream auction refused the bid. Legal from `relayed` (the
    // operator placed it and the house bounced it) and from `queued` (the
    // operator tried to place it and could not get it in at all).
    reject: ['relayed', 'queued'],
};
/**
 * Operator actions that require the acting admin to HOLD THE CLAIM on the row
 * (`claimedBy === admin.userId`), or to pass `force:true`, which is recorded on
 * the audit event. `queue` and `cancel-relay` are deliberately NOT in this
 * set: queueing is the cheap triage step, and a cancellation is a retraction
 * rather than a placement — neither is the "two operators place the same bid
 * twice" hazard the claim exists to close.
 */
export const RELAY_CLAIM_REQUIRED_ACTIONS = [
    'confirm-relay',
    'outcome',
    'reject',
];
/**
 * `POST /admin/relay-queue/:bidId/confirm-relay` body (Phase 13 — zod, not
 * hand-checked). `upstreamAmount` / `upstreamPlacedAt` record what ACTUALLY
 * stands on the real auction, which can legitimately differ from the client's
 * amount (rounding to the house's increment, a placement minutes later).
 */
/**
 * `upstreamRef` is OPTIONAL since 2026-09-28 (owner decision): the desk confirms
 * a relay with one click while the upstream fields are hidden. When one is sent
 * it must still be non-empty — a blank reference is refused, not stored.
 */
/**
 * Phase 15 (upstream-higher-bid, owner decision 2026-09-28): the operator may
 * tell the desk the REAL auction already stands at (or above) the client's
 * platform bid, instead of one click confirming it as-is.
 *
 *   upstreamHigherChf   — what the operator read on the real auction (X). Sent
 *                         ALONE, this is a QUERY: "may I still place this bid,
 *                         raised?" — the service answers either
 *                         409 UPSTREAM_BELOW_CLIENT_LIMIT (the client's own
 *                         auto-bid limit is above X — nothing is written) or
 *                         200 with the bid (and every platform bid ≤ X on the
 *                         lot) moved to Outbid, resynced to the real price.
 *   raiseToChf          — sent TOGETHER with `upstreamHigherChf`: the operator
 *                         acted on the 409's suggestion (or typed their own N)
 *                         and asks the desk to raise the client's bid to N —
 *                         authorised by the client's OWN auto-bid limit — and
 *                         relay it. Must be > upstreamHigherChf; the service
 *                         re-validates against the limit itself (never trusts
 *                         a client-sent limit).
 */
export const ConfirmRelayBodySchema = z
    .object({
    upstreamRef: z
        .string()
        .trim()
        .min(1, 'upstreamRef must not be blank — the bid number on the real auction')
        .optional(),
    upstreamAmount: z.number().positive().optional(),
    upstreamPlacedAt: z.coerce.date().optional(),
    secondApproverId: z.string().trim().min(1).optional(),
    /** Act without holding the claim — audited (`force:true`). */
    force: z.boolean().optional(),
    /** What the operator read on the real auction (Phase 15). */
    upstreamHigherChf: z.number().positive().optional(),
    /** The raised amount to relay — only legal together with `upstreamHigherChf`. */
    raiseToChf: z.number().positive().optional(),
})
    .refine((body) => body.raiseToChf === undefined || body.upstreamHigherChf !== undefined, {
    message: 'raiseToChf requires upstreamHigherChf',
    path: ['raiseToChf'],
})
    .refine((body) => body.raiseToChf === undefined ||
    body.upstreamHigherChf === undefined ||
    body.raiseToChf > body.upstreamHigherChf, { message: 'raiseToChf must be greater than upstreamHigherChf', path: ['raiseToChf'] });
/** `POST .../confirm-relay {upstreamHigherChf}` refusal — the client's own auto-bid limit beats it. */
export const UPSTREAM_BELOW_CLIENT_LIMIT = 'UPSTREAM_BELOW_CLIENT_LIMIT';
export const UPSTREAM_BELOW_CLIENT_LIMIT_ERROR_KEY = 'bids.relayQueue.errors.upstreamBelowClientLimit';
/**
 * `POST .../confirm-relay {upstreamHigherChf}` refusal (400): the operator
 * reported an upstream price BELOW the client's own platform bid. That is not
 * "the auction is already higher" — it is a plain confirm (or a typo), and
 * resyncing the lot to it would LOWER the platform's standing price.
 */
export const UPSTREAM_BELOW_PLATFORM_BID = 'UPSTREAM_BELOW_PLATFORM_BID';
export const UPSTREAM_BELOW_PLATFORM_BID_ERROR_KEY = 'bids.relayQueue.errors.upstreamBelowPlatformBid';
/**
 * `POST .../confirm-relay {upstreamHigherChf, raiseToChf}` refusal (409): the
 * raised amount no longer fits — above the client's own auto-bid limit, no
 * auto-bid at all, or no longer beating the lot's platform price. Nothing is
 * written.
 */
export const RAISE_NOT_AUTHORISED = 'RAISE_NOT_AUTHORISED';
export const RAISE_NOT_AUTHORISED_ERROR_KEY = 'bids.relayQueue.errors.raiseNotAuthorised';
/**
 * `cars.current_bid_user_id` after an upstream-outbid resync: the lot's
 * standing price is held by a bidder on the REAL auction, not by any platform
 * user. It must be a non-empty value — `AuctionEngineService.atomicAccept`
 * treats an ABSENT holder as "platform does not own the counter yet" and would
 * accept any amount, including one below the real price.
 */
export const UPSTREAM_LEADER_ID = 'upstream';
/** `cars` field holding the observed upstream price floor — see `CarSchema.upstream_price_floor`. */
export const UPSTREAM_PRICE_FLOOR_FIELD = 'upstream_price_floor';
/**
 * `cars` field holding the price the ingest bot last READ on the source page
 * (CHF). Written on every ingest that carries a positive bid. Unlike
 * `current_bid` it is never overwritten by a platform bid, and unlike
 * `upstream_price_floor` it may fall (the real auction moved) — it is a
 * reading, not a desk decision. Read by the bid minimum (`upstreamPriceOf`).
 */
export const UPSTREAM_SCRAPED_BID_FIELD = 'upstream_scraped_bid';
/** `POST /admin/relay-queue/:bidId/reject` body. */
export const RejectRelayBodySchema = z.object({
    reason: RelayRejectReasonSchema,
    note: z.string().trim().max(500).optional(),
    force: z.boolean().optional(),
});
/**
 * `POST /admin/relay-queue/:bidId/release` body. Releasing one's OWN claim
 * needs nothing; releasing a colleague's is an override and must say why.
 */
export const ReleaseClaimBodySchema = z.object({
    reason: z.string().trim().min(1).max(500).optional(),
});
/** `POST /admin/relay-queue/:bidId/outcome` body (Phase 13 adds `force`). */
export const RelayOutcomeBodySchema = z.object({
    outcome: z.enum(['won', 'lost']),
    forceReplace: z.boolean().optional(),
    force: z.boolean().optional(),
});
/**
 * Relay states from which an ADMIN may still record a win directly, i.e. the
 * `from` set of the single MarkWon use case when it is entered through
 * `PATCH /api/bids/:bidId/status {status:'Won'}` rather than the relay desk's
 * `POST /admin/relay-queue/:bidId/outcome`.
 *
 * DELIBERATELY WIDER than `RELAY_TRANSITIONS.outcome` (`['relayed']`): the
 * admin PATCH predates the relay desk and is still the way a win is recorded
 * for a lot that never went through it, so `captured` and `queued` must stay
 * legal. `won` keeps the PATCH idempotent (re-recording the same winner is a
 * no-op, not a 409), and `cancelled` keeps working after
 * `POST /api/admin/auction/:carId/cancel-all-bids`, which writes
 * `relayStatus:'cancelled'` on every live bid of the lot.
 *
 * `lost` is the ONE state excluded: a tender we already recorded as lost
 * upstream cannot also have been won, and silently overwriting that is the
 * data-integrity error this set exists to catch.
 */
export const MARK_WON_ADMIN_FROM_STATES = [
    'captured',
    'queued',
    'relayed',
    'won',
    'cancelled',
];
/**
 * Relay states in which the CLIENT may no longer change their bid amount.
 *
 * Once an admin has actually placed the bid on the real upstream auction
 * (`relayed`) — or the tender already resolved (`won` / `lost`) — the amount
 * standing upstream is a commitment we cannot retract. Letting the client
 * rewrite `bid` afterwards would silently desynchronise our record from the
 * real auction and, on a win, from the debt we bill. `captured` and `queued`
 * are still ours to change, so they stay editable.
 */
export const RELAY_LOCKED_STATES = ['relayed', 'won', 'lost'];
/**
 * True when a bid document's relay state forbids a client-side amount change.
 * Normalizes through `effectiveRelayStatus`, so a pre-relay document (no
 * `relayStatus` at all) is 'captured' → NOT locked.
 */
export function isRelayLocked(relayStatus) {
    return RELAY_LOCKED_STATES.includes(effectiveRelayStatus(relayStatus));
}
/**
 * True when the client may no longer change the bid at all — owner decision
 * 2026-10-11: a bid that stands on the real auction is FROZEN (no raise, no
 * retraction; only the operator acts on it).
 *
 * The platform learns that a bid stands upstream in TWO independent ways and
 * this is the one predicate that reads both:
 *   - the relay ladder — `relayed` / `won` / `lost` ({@link isRelayLocked});
 *   - the legacy «Оброблено» toggle — `processedOnAuction: true`, written by
 *     `PATCH /bids/:id/processed`, which does NOT move `relayStatus`. Before
 *     this predicate existed a processed-but-not-relayed bid read as unlocked,
 *     and a client withdrew a bid the operator had already placed.
 */
export function isBidFrozenForClient(bid) {
    return bid.processedOnAuction === true || isRelayLocked(bid.relayStatus);
}
/**
 * Why a relay was auto-cancelled by the platform rather than by an operator.
 * `outbid` — a higher bid on the same lot superseded it; `admin_cancel_all` —
 * the operator voided every live bid on the lot; `other_bid_won` — the lot was
 * awarded to a different bid, so this one can never be relayed (written by the
 * single MarkWon use case when it demotes the losing bids of a won lot);
 * `tender_closed_unrelayed` — the lot's live `auction_end_date` passed while
 * the bid was still captured/queued, so the operator never placed it upstream
 * (written by the Phase 10 TenderCloseService together with
 * `relayStatus: 'expired'`); `client_withdrawn` — the CLIENT retracted the bid
 * before the operator relayed it (Phase 13 `POST /api/bids/:id/withdraw`;
 * legal only from captured/queued, the same set the platform may auto-cancel).
 */
export const RELAY_CANCEL_REASONS = [
    'outbid',
    'admin_cancel_all',
    'other_bid_won',
    'tender_closed_unrelayed',
    'client_withdrawn',
    // Phase 15 (upstream-higher-bid, 2026-09-28): the operator told the desk the
    // REAL auction already stands at (or above) this bid — 'outbid', but by the
    // upstream house, not by another platform bid. Kept distinct from plain
    // 'outbid' so the audit trail (and any future client-facing copy) can say
    // WHY: the desk observed the real auction, it did not lose to a rival here.
    'outbid_upstream',
];
/**
 * Relay states the platform may auto-cancel. A bid already `relayed` is NOT
 * touched: the real bid stands upstream and only the operator can retract it.
 */
export const RELAY_AUTO_CANCELLABLE_STATES = ['captured', 'queued'];
/**
 * One line of the itemized client-debt breakdown.
 *
 * `auction_fee` stays in the enum ONLY so breakdowns stored before 2026-09-28
 * still parse: {@link computeClientDebt} no longer emits it (the platform fee
 * is CarsFinders' own cost), and every client-facing reader must ignore it.
 */
export const DebtBreakdownItemSchema = z.object({
    code: z.enum(['bid_amount', 'auction_fee', 'service_fee']),
    amount: z.number(),
    currency: z.enum(['CHF', 'EUR']),
});
/**
 * Itemized client debt generated when a relayed bid is marked 'won'.
 *
 * Totals stay PER CURRENCY and deliberately so: `totals.chf` is the Swiss leg
 * (the hammer price) and
 * `totals.eur` is the CarsFinders commission, settled with us. They are two
 * separate payments, and collapsing them into one number would silently
 * re-denominate every stored breakdown an invoice already rendered.
 */
export const DebtBreakdownSchema = z.object({
    items: z.array(DebtBreakdownItemSchema),
    totals: z.object({
        chf: z.number(),
        eur: z.number(),
    }),
    /** Which rule set produced the numbers (`customs.version`, e.g. ua-2026-02). */
    ruleVersion: z.string(),
    /**
     * The plan the commission was priced under. Optional: a breakdown stored
     * before Phase 7 has none, and back-filling one would be a guess.
     */
    planTier: PricingPlanTierSchema.optional(),
    computedAt: z.coerce.date(),
});
/**
 * THE client-debt calculation (Phase 7 — SC-8). Pure, deterministic given
 * `(rules, tier, bidChf, at)`.
 *
 *   bid            — the hammer price, CHF
 *   service_fee    — the CarsFinders commission, `resolvePlanSuccessFee` on the
 *                    EUR-converted hammer price for the client's plan tier.
 *
 * NO `auction_fee` item (owner decision 2026-09-28): the Swiss auction
 * platform fee (`resolveAuctionFeeChf`) is NOT charged to the client.
 * CarsFinders pays it out of its own service commission, so the client — and
 * every client-facing admin money view — sees only the hammer price and the
 * service commission, and `totals.chf` is the bid alone. The fee stays in the
 * rule set as CarsFinders' internal cost. Breakdowns stored before this change
 * may still carry an `auction_fee` item; readers summing a client-facing
 * figure must skip it.
 *
 * This replaced the Phase-9 relay fee defaults, which hardcoded 2.5%/CHF 200
 * and a flat
 * EUR 900 and therefore disagreed with every screen that quoted a commission.
 * Because both fee functions live in pricing-rules.ts, the bid-confirmation
 * modal, the landed-cost calculator and this invoice now quote the same money.
 */
export function computeClientDebt(rules, tier, bidChf, at = new Date()) {
    const bid = Math.max(0, bidChf);
    const plan = resolvePlanForTier(rules.plans, tier);
    const parsedTier = PricingPlanTierSchema.safeParse(tier);
    const planTier = parsedTier.success ? parsedTier.data : 'retail';
    const serviceFeeEur = resolvePlanSuccessFee(plan, bid * rules.swissExport.chfToEurRate);
    return {
        items: [
            { code: 'bid_amount', amount: bid, currency: 'CHF' },
            { code: 'service_fee', amount: serviceFeeEur, currency: 'EUR' },
        ],
        totals: {
            chf: bid,
            eur: serviceFeeEur,
        },
        ruleVersion: rules.customs.version,
        planTier,
        computedAt: at,
    };
}
/** SLA badge for the relay queue, derived from time-to-tender-close. */
export const SlaSeveritySchema = z.enum(['red', 'amber', 'green']);
/** Red < 1h to tender close (or overdue/unknown), amber < 4h, green otherwise. */
export function computeSlaSeverity(tenderCloseAt, now = new Date()) {
    // No close date = we cannot prove there is time left — treat as urgent.
    if (!tenderCloseAt || Number.isNaN(tenderCloseAt.getTime()))
        return 'red';
    const msLeft = tenderCloseAt.getTime() - now.getTime();
    if (msLeft < 60 * 60 * 1000)
        return 'red';
    if (msLeft < 4 * 60 * 60 * 1000)
        return 'amber';
    return 'green';
}
/**
 * Relay audit event payload — appended to the transactional outbox in the
 * SAME MongoDB transaction as the relayStatus flip (aggregate: 'relay',
 * type: 'relay.queued' | 'relay.confirmed' | 'relay.outcome' |
 * 'relay.cancelled').
 */
export const RelayAuditEventSchema = z.object({
    type: z.enum([
        'relay.queued',
        'relay.confirmed',
        'relay.outcome',
        'relay.cancelled',
        // Phase 13: the upstream auction refused the bid (relayed/queued →
        // upstream_rejected), and the two claim events. A claim is not a state
        // move, so on `relay.claimed` / `relay.released` previous === new.
        'relay.rejected',
        'relay.claimed',
        'relay.released',
        // Phase 15: the operator told the desk what the real auction stands at —
        // ALWAYS appended, in addition to whatever else the decision does
        // (relay.confirmed for a raise, relay.auto_cancelled per bid for an
        // upstream outbid), so "who checked X and decided what" is one row away
        // regardless of the outcome.
        'relay.upstream_checked',
    ]),
    data: z.object({
        bidId: z.string(),
        carId: z.string(),
        userId: z.string(),
        bidAmount: z.number(),
        /** Admin who performed the action. */
        who: z.string(),
        /** ISO timestamp of the action. */
        when: z.string(),
        previousRelayStatus: RelayStatusSchema,
        newRelayStatus: RelayStatusSchema,
        /** Bid number on the real upstream auction (confirm-relay only). */
        upstreamRef: z.string().nullish(),
        /** FOUR-EYES-READY: optional second confirmer recorded, not yet required. */
        secondApproverId: z.string().nullish(),
        /** 'won' | 'lost' (outcome action only). */
        outcome: z.string().nullish(),
        /** Itemized client debt (outcome 'won' only). */
        debtBreakdown: DebtBreakdownSchema.nullish(),
        /**
         * Phase 10: present when the exposure gate would have rejected the relay
         * but EXPOSURE_ENFORCED is off (warn-only mode).
         */
        exposureWarning: ExposureWarningSchema.nullish(),
        /** LEGACY (pre-Phase 10 rows): the biddingPower × 10 deposit gate warning. */
        depositWarning: z
            .object({
            biddingPower: z.number(),
            maxAllowedBid: z.number(),
            requestedAmount: z.number(),
        })
            .nullish(),
        // ── Phase 13 ──────────────────────────────────────────────────────────
        /** Amount actually standing on the real auction (confirm-relay only). */
        upstreamAmount: z.number().nullish(),
        /** ISO timestamp of the upstream placement (confirm-relay only). */
        upstreamPlacedAt: z.string().nullish(),
        /** Why the upstream auction refused the bid (reject only). */
        rejectReason: RelayRejectReasonSchema.nullish(),
        rejectNote: z.string().nullish(),
        /** True when the admin acted WITHOUT holding the claim (`force:true`). */
        force: z.boolean().nullish(),
        /** Who held the claim when a forced action or a forced release happened. */
        claimedBy: z.string().nullish(),
        /** Reason given for releasing a colleague's claim (release override only). */
        releaseReason: z.string().nullish(),
        // ── Phase 15 (upstream-higher-bid) ──────────────────────────────────────
        /** What the operator read on the real auction (`relay.upstream_checked` only). */
        upstreamHigherChf: z.number().nullish(),
        /** 'outbid' (3c: resynced to X, bid(s) ≤ X moved to Outbid) or 'raise' (3b: raised to `raiseToChf`). */
        decision: z.enum(['outbid', 'raise']).nullish(),
        /** This bid owner's own auto-bid limit, when one applied to the decision. */
        clientLimitChf: z.number().nullish(),
        /** The amount actually relayed after a raise (`relay.upstream_checked` + 'raise' only). */
        raiseToChf: z.number().nullish(),
    }),
});
/** One row of GET /api/admin/relay-queue — badge-ready, no client recompute. */
export const RelayQueueItemSchema = z.object({
    bidId: z.string(),
    carId: z.string(),
    userId: z.string(),
    bid: z.number(),
    maxBid: z.number().nullish(),
    relayStatus: RelayStatusSchema,
    /** The LIVE `cars.auction_end_date` (Phase 13), falling back to the capture snapshot. */
    tenderCloseAt: z.coerce.date().nullable(),
    slaSeverity: SlaSeveritySchema,
    createdAt: z.coerce.date().nullish(),
    // ── Phase 13 operator claim (additive — pre-13 clients ignore them) ─────
    /** Admin userId holding the row, or null. */
    claimedBy: z.string().nullish(),
    /** MASKED email of the claimer (`j***@x.com`) — never a full address on the wire. */
    claimedByLabel: z.string().nullish(),
    claimedAt: z.coerce.date().nullish(),
    car: z
        .object({
        brand: z.string().nullish(),
        model: z.string().nullish(),
        listing_id: z.string().nullish(),
        current_bid: z.unknown().nullish(),
    })
        .nullable(),
    user: z
        .object({
        email: z.string().nullish(),
        biddingPower: z.number().nullish(),
    })
        .nullable(),
});
//# sourceMappingURL=relay.js.map